/**
 * The delta distribution chart (SPEC.md §7.1.3, §8.7).
 *
 * A histogram of `delta_days`, bucketed by day, zero-centred. "A task consistently landing
 * four days late does not mean the owner is failing, it means the interval is wrong" — the
 * insight only a distribution gives; a streak cannot produce it.
 *
 * Every bar is the same neutral colour, deliberately. §7.1.3 is explicit that the delta is
 * descriptive and never a score — colouring early bars one way and late bars another would
 * say "good" and "bad" in exactly the register the section rules out, however careful the
 * copy around it is.
 */

import type { TaskHistory } from "../api";

export function DeltaHistogram({ history }: { history: TaskHistory }) {
  const buckets = Object.entries(history.delta_histogram)
    .map(([delta, count]) => [Number(delta), count] as const)
    .sort((a, b) => a[0] - b[0]);

  if (buckets.length === 0) {
    return <p className="small muted">No completions yet - the chart fills in as you go.</p>;
  }

  const max = Math.max(...buckets.map(([, count]) => count));

  return (
    <div className="delta-histogram">
      <div
        className="delta-histogram-bars"
        role="img"
        aria-label="Distribution of how early or late this task is usually completed, zero centred on the due day"
      >
        {buckets.map(([delta, count]) => (
          <div
            key={delta}
            className="delta-histogram-bar"
            title={
              delta === 0
                ? `${count} on time`
                : delta > 0
                  ? `${count} ${count === 1 ? "completion" : "completions"}, ${delta}d early`
                  : `${count} ${count === 1 ? "completion" : "completions"}, ${-delta}d late`
            }
          >
            <span
              className={`delta-histogram-fill${delta === 0 ? " on-time" : ""}`}
              style={{ height: `${Math.max(10, (count / max) * 100)}%` }}
            />
            <span className="delta-histogram-label">{delta}</span>
          </div>
        ))}
      </div>
      <p className="small muted" style={{ margin: "0.35rem 0 0" }}>
        Days early (+) or late (−) - 0 is on time.
      </p>
    </div>
  );
}
